// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  LMCubeDetailSectionPVPeriodEvent,
  LMListDragDropEvent,
  LMListSelectionEvent,
  LMListTransactionEvent
} from './lm-list.component';
import { LMDetailSectionBase } from './lm-detail-section-base';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { AppConstants } from '@utils/app.constants';
import { Observable, Observer, of } from 'rxjs';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMContextService , LMUrlInfoBuilderService} from '@services/index';
import { LMRefreshRequest } from '@app/components';

@Component({
  selector:    'lm-detail-section-list',
  templateUrl: './lm-detail-section-list.component.html',
  styleUrls:   [ './lm-detail-section-list.component.scss' ],
})
export class LMDetailSectionListComponent implements LMDetailSectionBase, AfterViewInit, OnDestroy, OnInit {
  @HostBinding('attr.id') get classes() {
    return this.detailSectionId;
  };

  @ViewChild(SohoBusyIndicatorDirective) sohoBusyIndicator: SohoBusyIndicatorDirective;
  @ViewChild(forwardRef(() => 'filterListComponent')) lmFilterListComponent; // : LMListComponent;
  @ViewChild(forwardRef(() => 'listComponent')) lmListComponent; // : LMListComponent;

  // --------------------------------------------
  // INPUTS
  // --------------------------------------------
  @Input()
  set panel(panel: LandmarkPanel) {
    this._panel = panel;

    let targetListAdditionalUrlFields: string[] = [];

    if (panel.filterListSpecs && panel.filterListSpecs.length > 0) {
      if (panel.filterListSpecs.length !== 1) {
        throw 'LMDetailSectionListComponent: only a single filter list is supported';
      }

      const filterListSpec = panel.filterListSpecs[ 0 ];

      // ------------------------------------------------------------------------
      // target list needs teh LMFilterListSpec.fieldId (i.e. the thing in the
      // filter-list used to match with the target-list).
      // ------------------------------------------------------------------------
      const filterListAdditionalUrlFields = filterListSpec.filterUsing.map(filterUsing => filterUsing.value.fieldId);
      if (filterListSpec.groupLabel) {
        filterListAdditionalUrlFields.push(filterListSpec.groupLabel.fieldId);
      }

      this.filterListSpec = filterListSpec;
      this.filterListUrlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(
        filterListSpec, { additionalFieldIds: filterListAdditionalUrlFields });

      // ------------------------------------------------------------------------
      // target list needs teh LMFilterListSpec.value.fieldId (i.e. the thing
      // you are matching on the in the target-list).
      // ------------------------------------------------------------------------
      targetListAdditionalUrlFields = filterListSpec.filterUsing.map(filterUsing => filterUsing.fieldId);
    }

    this.listSpec = panel.listSpec;
    this.listUrlInfo = this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(
      panel.listSpec, { additionalFieldIds: targetListAdditionalUrlFields });

    this.buildDetailSectionId(this.panel); // build the id once the panel comes in
  }

  get panel(): LandmarkPanel {
    return this._panel;
  }

  @Input() set fromDataView(fromDataView: LMDataView) {
    this._fromDataView = fromDataView;

    // ------------------------------------------------------------------------
    // reset the filter criteria when the from dataView has changed. This will
    // prevent a bad combination between the valid filter list values and the
    // fromDataView.
    // ------------------------------------------------------------------------
    this.updatedHasFilterListFlag();
    if (this.hasFilterList) {
      this.filterListFilterCriteria = null;
      if (this.filterListSpec.implementsDragAndDrop) {
        this.implementsDragAndDrop = true;
      }
    }
  }

  @Input() needsReload: boolean;
  @Input() activated: boolean;
  // private _activated = false;
  // @Input() set activated(activated: boolean) {
  //   this._activated = activated;
  // }
  // get activated() {
  //   return this._activated;
  // }

  // --------------------------------------------
  // OUTPUTS
  // --------------------------------------------
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() detailSectionDisplayed = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<LMListFieldChangedEvent>();
  @Output() updateToolbarStateEvent = new EventEmitter<any>();
  @Output() panelDataUpdated = new EventEmitter<any>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();
  @Output() cubeDetailSectionPVPeriodEvent = new EventEmitter<LMCubeDetailSectionPVPeriodEvent>();

  // --------------------------------------------
  // VARIABLES
  // --------------------------------------------
  private _fromDataView: LMDataView = null;
  private _panel: LandmarkPanel = null;
  private detailSectionId: string = null;
  private listDetail: any;
  readonly instanceId: string;

  public testVersion1: boolean;

  public listUrlInfo: LMListUrlInfo;
  public listSpec: LMListSpec;

  public filterListUrlInfo: LMListUrlInfo;
  public filterListSpec: LMFilterListSpec;
  public hasFilterList = false;
  public implementsDragAndDrop = false;
  public filterListFilterCriteria: { filterDataView: LMDataView, filterCriteria: LMFilterFieldCriteria[] };
  public uiLoadingIndicatorTimeout = 0;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private lmConditionUtilService: LMConditionUtilService,
    private lmContextService: LMContextService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmLoggerService: LMLoggerService,
    private lmBusyIndicatorService: LMBusyIndicatorService
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngOnInit() {
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
  }
  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);
  }

  ngOnDestroy() {
    if (this.listDetail) {
      this.listDetail.destroy();
    }
  }

  // --------------------------------------------
  // GETTERS
  // --------------------------------------------
  // get listSpec(): LMListSpec {
  //   return this.panel.listSpec;
  // }
  //
  get fromDataView(): LMDataView {
    if (this.needsReload || (this._fromDataView && this._fromDataView.isNewRow)) {
      return null;
    }
    return this._fromDataView;
  }

  get type() {
    return 'list';
  }

  private updatedHasFilterListFlag(): boolean {
    if (!this.panel.filterListSpecs || this.panel.filterListSpecs.length === 0) {
      this.hasFilterList = false;
      return;
    }

    this.hasFilterList = this.lmConditionUtilService.isValid(this.panel.filterListSpecs[ 0 ].validity, this.fromDataView);
  }

  // get filterListSpec(): LMFilterListSpec {
  //   return this.panel.filterListSpecs[ 0 ];
  // }

  // --------------------------------------------
  // EVENT HANDLERS
  // --------------------------------------------
  onFilterListRowSelected(event: LMListSelectionEvent): void {
    if (!this.lmFilterListComponent) {
      this.lmListComponent.disabled = true;
      return;
    }

    const selectedRow = this.lmFilterListComponent.getSelectedRow();
    if (selectedRow === undefined || selectedRow === -1) {
      this.lmListComponent.disabled = true;
      return;
    }

    const filterListDataView = this.lmFilterListComponent.getDataView(selectedRow);
    if (!filterListDataView) {
      this.lmListComponent.disabled = true;
      return;
    }

    this.lmLoggerService.log([ this, this.instanceId, 'Filter List Index Selected ' + event.selectedRows[ 0 ].idx + ', building filter criteria' ]);

    // call in a timeout since soho event is outside an angular zone
    setTimeout(() => this.buildFilterListCriteria(filterListDataView), 1);
  }

  onFilterListDisplayed(event: any) {
    if (this.hasFilterList) {
      if (this.lmFilterListComponent.getDataViewSet().data.length === 0) {
        // ---------------------------------------------------------------------
        // causes any data in the filter-lists target list to be cleared.
        // ---------------------------------------------------------------------
        this.filterListFilterCriteria = null;
      }
    }
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onListTransactionEvent(event: LMListTransactionEvent) {
    // Do not refresh filter list itself, let its parent send the update event
    // if (this.lmFilterListComponent) {
    //   this.lmFilterListComponent.refresh().subscribe(result => {
    //   });
    // }
    this.panelDataUpdated.emit(event);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    this.drillAroundEvent.emit(event);
  }

  onBusyIndicatorDisplayed(event: LMBusyIndicatorDisplayedEvent) {
    if (event.show && !this.sohoBusyIndicator.isActive()) {
      this.lmBusyIndicatorService.showBusyIndicator(this.sohoBusyIndicator);
    } else if (!event.show && this.sohoBusyIndicator.isActive()) {
      this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
    }
  }

  onContentModified(event: LMListFieldChangedEvent) {
    this.contentModified.emit(event);
  }

  onUpdateToolbarStateEvent(event: any) {
    this.updateToolbarStateEvent.emit();
  }

  onListDisplayed(event: any) {
    this.detailSectionDisplayed.emit(event);

    if (this.hasFilterList) {
      const $detailSectionPanel = $('#' + this.detailSectionId + ' .list-detail');

      // only initialize once.
      if (!this.listDetail) {
        const listElement = '#' + this.detailSectionId + ' .accordion';
        const detailElement = '#' + this.detailSectionId + ' .filterlist-content-area';
        const backElement = '#' + this.detailSectionId + ' .list-detail-back-button';
        const listCloseElement = '#' + this.detailSectionId + ' .list-detail-list-close-button';

        this.listDetail = $detailSectionPanel.listdetail({
          listElement,
          detailElement,
          backElement,
          listCloseElement,
          edgeBleed:           true,
          edgeBleedBreakpoint: 'tablet'
        }).data('listdetail');
      }
    }
  }

  onCubeDetailSectionPVPeriodEvent(event: LMCubeDetailSectionPVPeriodEvent) {
    this.cubeDetailSectionPVPeriodEvent.emit(event);
  }

  // --------------------------------------------
  // PRIVATE FUNCTIONS
  // --------------------------------------------
  private buildFilterListCriteria(selectedDataView: LMDataView) {
    if (this.filterListFilterCriteria && this.filterListFilterCriteria.filterDataView.resourceId === selectedDataView.resourceId) {
      // if the dataView for the fitlerListCriteria hasn't change then don't
      // build a new filterListFilterCriteria object so the filter list target
      // doesn't load more than it has to.
      return;
    }

    const filterCriteria: LMFilterFieldCriteria[] = this.filterListSpec.filterUsing.map(filterUsing => {
      const filterFieldCriteria: LMFilterFieldCriteria = {
        operator: 'equals',
        fieldId:  filterUsing.fieldId,
        value:    selectedDataView.fields[ filterUsing.value.fieldId ].value.toString()
      };
      return filterFieldCriteria;
    });

    this.filterListFilterCriteria = {
      filterDataView: selectedDataView,
      filterCriteria
    }
  }

  // --------------------------------------------
  // INFRASTRUCTURE
  // --------------------------------------------
  isModified(): boolean {
    if (!this.lmListComponent) {
      return false;
    }

    return this.lmListComponent.isModified();
  }

  refresh(request: LMRefreshRequest): Observable<boolean> {
    if (this.hasFilterList && this.lmFilterListComponent) {
      return this.lmFilterListComponent.refresh(request);
    }

    if (this.lmListComponent?.listId !== request?.originatingSourceId) {
      // console.log('list-detail-section refresh: ' + this.lmListComponent?.listSpec.busListSpec.name);
      return this.lmListComponent.refresh(request);
    } else {
      // console.log('list-detail-section refresh canceled: ' + this.lmListComponent?.listSpec.busListSpec.name);
      return of(true);
    }
  }

  // private _refreshList(request: LMRefreshRequest): Observable<boolean> {
  //   if (this.lmListComponent?.listId !== request?.originatingSourceId) {
  //     console.log('list-detail-section refresh: ' + this.lmListComponent?.listSpec.busListSpec.name);
  //     return this.lmListComponent.refresh(request);
  //   } else {
  //     console.log('list-detail-section refresh canceled: ' + this.lmListComponent?.listSpec.busListSpec.name);
  //     return of(true);
  //   }
  // }

  save(callBack: Observer<boolean>) {
    this.lmListComponent.runSaveAction(AppConstants.STANDARD_SAVE, callBack);
  }

  saveDetailSectionState() {
    if (this.lmFilterListComponent) {
      this.lmFilterListComponent.saveComponentState()
    }
    if (this.lmListComponent) {
      this.lmListComponent.saveComponentState();
    }
  }

  onListDragDropEvent(event: LMListDragDropEvent) {
    if (this.lmFilterListComponent) {
      const rows: Array<number> = (event.row) || event.row === 0 ? [ event.row ] : this.lmListComponent.getSelectedRows();
      this.lmListComponent.runInstanceAction(this.filterListSpec.dragAndDrop.actionSpec, this.lmListComponent.getDataViewSet(), rows, null,
        null, this.filterListSpec.dragAndDrop.actionFieldMap, this.lmFilterListComponent.getDataView(event.filterRow));
    }
  }

  private buildDetailSectionId(panel: LandmarkPanel) {
    this.detailSectionId = panel.id + '_panel';
  }

  clearDetailSectionState() {
    if (this.lmFilterListComponent) {
      this.lmFilterListComponent.clearComponentState()
    }
    if (this.lmListComponent) {
      this.lmListComponent.clearComponentState();
    }
  }
}
